/** @forge-header
 * @id          kalshiApiPositions
 * @role        Portfolio positions + fills | polymorphic response normalization (5+ shapes)
 * @pattern     adapter
 * @purity      api
 * @units       mixed
 * @money-path  yes
 * @upstream    kalshiApi/client:authenticatedFetch | kalshiApi/types:KalshiOrder,KalshiFill,KalshiPosition
 * @downstream  kalshiApi/index (via createKalshiClient)
 * @exports     getPositions:fn | getFilledOrders:fn | normalizeKalshiPositions:fn
 * @test        untested (integration-level)
 *
 * @invariant   position-sign | position > 0 = YES exposure, position < 0 = NO exposure (when side absent)
 * @danger      normalizeKalshiPositions handles 5+ Kalshi response shapes | test any API version changes
 */

import type { KalshiOrder, KalshiFill, KalshiPosition } from './types';
import { authenticatedFetch } from './client';
import { dollarsToCents, fpToInt } from './parseHelpers';
import { boundaryWarn } from '../infra/boundaryLog';

/**
 * Convert order to fill (simplified - real API might have separate fill endpoint)
 */
function orderToFill(order: KalshiOrder): KalshiFill {
  // @ai-note 2026-03-12: Prefer _dollars/_fp fields, fall back to legacy.
  const raw = order as unknown as Record<string, unknown>;
  const price =
    dollarsToCents(
      order.side === 'yes' ? raw.yes_price_dollars : raw.no_price_dollars,
      order.side === 'yes' ? order.yes_price : order.no_price
    ) ??
    (order.side === 'yes' ? order.yes_price : order.no_price) ??
    0;
  const count = fpToInt(raw.remaining_count_fp, order.remaining_count) ?? order.remaining_count;
  return {
    fill_id: order.order_id,
    order_id: order.order_id,
    ticker: order.ticker,
    side: order.side,
    action: order.action,
    count,
    price,
    created_at: order.created_time,
  };
}

/**
 * Normalize Kalshi "positions" payloads into our internal KalshiPosition[] shape.
 * Kalshi has had multiple response shapes over time (and can differ by endpoint/version).
 */
export function normalizeKalshiPositions(raw: unknown): KalshiPosition[] {
  const asRecord = (v: unknown): Record<string, unknown> | null =>
    v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
  const toNumber = (v: unknown): number | null => {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };
  const toSide = (v: unknown): 'yes' | 'no' | null => {
    if (typeof v !== 'string') return null;
    const s = v.toLowerCase();
    if (s === 'yes' || s === 'no') return s;
    return null;
  };

  // Unwrap common response envelopes
  const rootRec = asRecord(raw);
  let items: unknown = raw;
  if (rootRec) {
    items =
      // Newer/alternate shapes (seen in prod): { cursor, market_positions, event_positions }
      rootRec.market_positions ??
      rootRec.marketPositions ??
      rootRec.event_positions ??
      rootRec.eventPositions ??
      // Older/common envelopes
      rootRec.positions ??
      rootRec.results ??
      rootRec.data ??
      rootRec.portfolio_positions ??
      rootRec.portfolioPositions ??
      rootRec.position ??
      raw;

    // Sometimes positions are nested like { positions: { positions: [...] } }
    const itemsRec = asRecord(items);
    if (itemsRec && (itemsRec.positions || itemsRec.results || itemsRec.data)) {
      items = itemsRec.positions ?? itemsRec.results ?? itemsRec.data;
    }
  }

  // Kalshi /portfolio/positions returns both market_positions (per-market detail)
  // and event_positions (event-level summaries with no position_fp/ticker).
  // Use market_positions when available; only fall back to event_positions.
  if (rootRec) {
    const marketItems = rootRec.market_positions ?? rootRec.marketPositions;
    const eventItems = rootRec.event_positions ?? rootRec.eventPositions;
    if (Array.isArray(marketItems)) {
      items = marketItems;
    } else if (Array.isArray(eventItems)) {
      items = eventItems;
    }
  }

  if (!Array.isArray(items)) {
    return [];
  }

  const out: KalshiPosition[] = [];

  for (const item of items) {
    const rec = asRecord(item);
    if (!rec) continue;

    const ticker =
      (typeof rec.ticker === 'string' && rec.ticker) ||
      (typeof rec.market_ticker === 'string' && rec.market_ticker) ||
      (typeof rec.marketTicker === 'string' && rec.marketTicker) ||
      (typeof rec.event_ticker === 'string' && rec.event_ticker) ||
      (typeof rec.eventTicker === 'string' && rec.eventTicker) ||
      (typeof rec.symbol === 'string' && rec.symbol) ||
      null;

    // Shape A2: signed position without explicit side (common in Kalshi market_positions)
    // Convention: position > 0 => YES exposure, position < 0 => NO exposure.
    if (ticker) {
      const signed = toNumber(rec.position_fp ?? rec.position);
      if (signed !== null && signed !== 0 && rec.side === undefined) {
        const inferredSide: 'yes' | 'no' = signed < 0 ? 'no' : 'yes';
        const qty = Math.abs(signed);

        // Try to derive avg price from exposure / qty when avg_price isn't provided.
        const exposure =
          toNumber(
            rec.market_exposure ?? rec.marketExposure ?? rec.total_cost ?? rec.totalCost ?? rec.cost
          ) ??
          toNumber(
            rec.market_exposure_dollars ??
              rec.marketExposureDollars ??
              rec.total_cost_dollars ??
              rec.totalCostDollars ??
              rec.cost_dollars ??
              rec.costDollars
          );

        const avg =
          toNumber(
            rec.average_price_dollars ??
              rec.average_price ??
              rec.avg_price ??
              rec.avgPrice ??
              rec.averagePrice
          ) ?? (exposure !== null && qty > 0 ? exposure / qty : 0);

        const realized =
          toNumber(
            rec.realized_pnl ??
              rec.realizedPnl ??
              rec.realized_pnl_dollars ??
              rec.realizedPnlDollars
          ) ?? 0;

        out.push({
          ticker,
          side: inferredSide,
          position: qty,
          average_price: avg,
          realized_pnl: realized,
        });
        continue;
      }
    }

    // Shape A: explicit side + position
    const side = toSide(rec.side);
    const position = toNumber(
      rec.position_fp ??
        rec.position ??
        rec.quantity_fp ??
        rec.quantity ??
        rec.count_fp ??
        rec.count
    );

    if (ticker && side && position !== null) {
      const avg =
        toNumber(
          rec.average_price_dollars ??
            rec.average_price ??
            rec.avg_price ??
            rec.avgPrice ??
            rec.averagePrice ??
            rec.entry_price ??
            rec.entryPrice
        ) ?? 0;
      const realized =
        toNumber(
          rec.realized_pnl ?? rec.realizedPnl ?? rec.realized_pnl_dollars ?? rec.realizedPnlDollars
        ) ?? 0;

      out.push({
        ticker,
        side,
        position,
        average_price: avg,
        realized_pnl: realized,
      });
      continue;
    }

    // Shape B: combined yes/no positions per ticker
    if (ticker) {
      const yesPos = toNumber(
        rec.yes_position ??
          rec.yesPosition ??
          rec.yes_qty ??
          rec.yesQty ??
          rec.yes_quantity ??
          rec.yesQuantity ??
          rec.yes_contracts ??
          rec.yesContracts
      );
      const noPos = toNumber(
        rec.no_position ??
          rec.noPosition ??
          rec.no_qty ??
          rec.noQty ??
          rec.no_quantity ??
          rec.noQuantity ??
          rec.no_contracts ??
          rec.noContracts
      );

      if (yesPos !== null) {
        const yesAvg =
          toNumber(
            rec.yes_average_price ??
              rec.yes_avg_price ??
              rec.yesAvgPrice ??
              rec.average_price ??
              rec.avg_price
          ) ?? 0;
        const yesRealized =
          toNumber(
            rec.yes_realized_pnl ?? rec.yesRealizedPnl ?? rec.realized_pnl ?? rec.realizedPnl
          ) ?? 0;
        out.push({
          ticker,
          side: 'yes',
          position: yesPos,
          average_price: yesAvg,
          realized_pnl: yesRealized,
        });
      }

      if (noPos !== null) {
        const noAvg =
          toNumber(
            rec.no_average_price ??
              rec.no_avg_price ??
              rec.noAvgPrice ??
              rec.average_price ??
              rec.avg_price
          ) ?? 0;
        const noRealized =
          toNumber(
            rec.no_realized_pnl ?? rec.noRealizedPnl ?? rec.realized_pnl ?? rec.realizedPnl
          ) ?? 0;
        out.push({
          ticker,
          side: 'no',
          position: noPos,
          average_price: noAvg,
          realized_pnl: noRealized,
        });
      }
    }
  }

  // Shape assertion: warn on positions that couldn't be parsed by any shape.
  // Shape A2/A use position_fp/position; Shape B uses yes_position/no_position.
  for (const item of items) {
    const rec = asRecord(item);
    if (!rec) continue;
    const hasPositionField =
      rec.position_fp !== undefined ||
      rec.position !== undefined ||
      rec.yes_position !== undefined ||
      rec.yesPosition !== undefined ||
      rec.no_position !== undefined ||
      rec.noPosition !== undefined;
    if (!hasPositionField) {
      boundaryWarn('api-shape', 'Position item has no recognized position field', {
        ticker: rec.ticker ?? rec.market_ticker ?? rec.event_ticker ?? 'unknown',
        keys: Object.keys(rec).slice(0, 15),
      });
    }
  }

  // Filter out zero positions to match what users expect to see in a Positions table.
  return out.filter((p) => Number.isFinite(p.position) && p.position !== 0);
}

/**
 * Get positions
 */
export async function getPositions(): Promise<KalshiPosition[]> {
  try {
    const response = await authenticatedFetch('GET', '/trade-api/v2/portfolio/positions');
    const data = await response.json();

    const positions = normalizeKalshiPositions(data);
    return positions;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (
      message.includes('API error: 401') ||
      message.includes('API error: 403') ||
      message.includes('"code":"authentication_error"') ||
      message.includes('"code": "authentication_error"')
    ) {
      throw error;
    }
    console.warn('Failed to fetch positions:', error);
    return [];
  }
}

/**
 * Parse a fills API response into KalshiFill[].
 * Handles { fills: [...] }, { results: [...] }, and bare arrays.
 */
function parseFillsResponse(data: unknown): KalshiFill[] {
  if (!data || typeof data !== 'object') return [];
  const d = data as Record<string, unknown>;
  const arr = Array.isArray(d)
    ? d
    : Array.isArray(d.fills)
      ? d.fills
      : Array.isArray(d.results)
        ? d.results
        : null;
  if (!arr) return [];

  return (arr as Array<Record<string, unknown>>).map((f) => {
    const raw = f;
    const side = (raw.side as string) || 'yes';
    const price =
      dollarsToCents(
        side === 'yes' ? raw.yes_price_dollars : raw.no_price_dollars,
        side === 'yes' ? raw.yes_price : raw.no_price
      ) ?? (typeof raw.price === 'number' ? raw.price : 0);
    const count =
      fpToInt(raw.count_fp, raw.count) ?? (typeof raw.count === 'number' ? raw.count : 0);
    return {
      fill_id: (raw.trade_id ?? raw.fill_id ?? raw.id ?? '') as string,
      order_id: (raw.order_id ?? '') as string,
      ticker: (raw.ticker ?? '') as string,
      side: side as 'yes' | 'no',
      action: (raw.action ?? 'buy') as 'buy' | 'sell',
      count,
      price,
      count_fp: raw.count_fp as string | undefined,
      price_dollars: raw.price_dollars as string | undefined,
      yes_price_dollars: raw.yes_price_dollars as string | undefined,
      no_price_dollars: raw.no_price_dollars as string | undefined,
      created_at: (raw.created_time ?? raw.created_at ?? '') as string,
    };
  });
}

/**
 * Get filled orders / trade executions.
 *
 * Since Feb 2026, Kalshi moved older orders out of /portfolio/orders.
 * We now use /portfolio/fills (recent) + /historical/fills (older)
 * to get actual fill execution records, falling back to the legacy
 * /portfolio/orders?status=filled for compatibility.
 */
export async function getFilledOrders(
  params: { cursor?: string; limit?: number } = {}
): Promise<KalshiFill[]> {
  const query: Record<string, string> = {};
  if (params.cursor) query.cursor = params.cursor;
  if (params.limit) query.limit = params.limit.toString();

  // Try /portfolio/fills first (Feb 2026+ API)
  try {
    const response = await authenticatedFetch('GET', '/trade-api/v2/portfolio/fills', query);
    const data = await response.json();
    const fills = parseFillsResponse(data);
    if (fills.length > 0) return fills;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (
      message.includes('API error: 401') ||
      message.includes('API error: 403') ||
      message.includes('"code":"authentication_error"') ||
      message.includes('"code": "authentication_error"')
    ) {
      throw error;
    }
    // Non-auth error — endpoint may not exist, fall through
  }

  // Try /historical/fills (for older settled fills)
  try {
    const response = await authenticatedFetch('GET', '/trade-api/v2/historical/fills', query);
    const data = await response.json();
    const fills = parseFillsResponse(data);
    if (fills.length > 0) return fills;
  } catch {
    // Fall through to legacy
  }

  // Legacy fallback: /portfolio/orders?status=filled
  try {
    const legacyQuery: Record<string, string> = { status: 'filled', ...query };
    const response = await authenticatedFetch('GET', '/trade-api/v2/portfolio/orders', legacyQuery);
    const data = await response.json();
    if (Array.isArray(data)) {
      return data.filter((o: KalshiOrder) => o.status === 'filled').map(orderToFill);
    }
    if (data.orders && Array.isArray(data.orders)) {
      return data.orders.filter((o: KalshiOrder) => o.status === 'filled').map(orderToFill);
    }
    return [];
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (
      message.includes('API error: 401') ||
      message.includes('API error: 403') ||
      message.includes('"code":"authentication_error"') ||
      message.includes('"code": "authentication_error"')
    ) {
      throw error;
    }
    console.warn('Failed to fetch filled orders:', error);
    return [];
  }
}
